import { observer } from 'mobx-react-lite';
import React from 'react';
import clsx from 'clsx';
import {
  FocusHandler,
  FocusContext,
  setFocus,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import { IS_BROWSER } from '@smart-tv/utils/is-browser';

import Button from '@smart-tv/components/button/button';
import DragScroll from '@smart-tv/components/drag-scroll/drag-scroll';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import { ArrowDownIcon } from '@smart-tv/components/icons/arrow-down';
import styles from '../episode-selector.module.scss';
import { rootStore } from '../_stores/store-root';
import { Seasons } from '../_stores/store-seasons';
import { ALL_EPISODES_LIMIT } from '../_stores/store-episodes';
import { FocusKeysSheet } from '../sheet-helpers';

const SEASON_TAB_FOCUS_KEY_ALL = 'EPISODE_SELECTOR_SEASON_ALL';
const seasonTabFocusKey = (id: number) => `EPISODE_SELECTOR_SEASON_${id}`;

export { SEASON_TAB_FOCUS_KEY_ALL, seasonTabFocusKey };

// ─── Season tabs row ──────────────────────────────────────────

interface SheetEpisodeSelectorSeasonTabsProps {
  hidden?: boolean;
}

const SheetEpisodeSelectorSeasonTabs = observer(
  ({ hidden = false }: SheetEpisodeSelectorSeasonTabsProps) => {
    const { seasons, selectedSeasonId } = rootStore.seasonsStore;
    const { sheet } = rootStore.sheetStore;

    const preferredChildFocusKey =
      selectedSeasonId === null ? SEASON_TAB_FOCUS_KEY_ALL : seasonTabFocusKey(selectedSeasonId);

    const handleSeasonTabArrowPress = React.useCallback((direction: string) => {
      if (direction === 'up') {
        setFocus('EPISODE_SELECTOR_TAB_EPISODES');

        return false;
      }

      return true;
    }, []);

    const handleFirstSeasonTabArrowPress = React.useCallback(
      (direction: string) => {
        if (direction === 'left') return false;
        return handleSeasonTabArrowPress(direction);
      },
      [handleSeasonTabArrowPress]
    );

    const { ref, focusKey } = useFocusable({
      trackChildren: true,
      preferredChildFocusKey,
      focusKey: FocusKeysSheet.EPISODE_SELECTOR_SEASON_TABS
    });

    const handleSeasonTabFocus = React.useCallback<FocusHandler>(
      ({ node }) => {
        if (ref.current) {
          // Use the node's own offsetLeft (stable) rather than the scroll-relative x from the layout,
          // which would compound drift across successive focus moves.
          ref.current.scrollLeft = Math.max(0, node.offsetLeft - ref.current.clientWidth / 3);
        }
      },
      [ref]
    );

    React.useEffect(() => {
      const element = ref.current as HTMLDivElement | null;
      const selectedTab = element?.querySelector<HTMLElement>('.button--selected');
      if (element && selectedTab) {
        element.scrollLeft = Math.max(0, selectedTab.offsetLeft - element.clientWidth / 3);
      }
    }, [hidden, seasons, selectedSeasonId, ref]);

    const allSeasonIds = React.useMemo(() => seasons.map((s: Seasons) => s.seasonIds), [seasons]);

    const fetchForSeason = React.useCallback(
      (seasonIds: number[], filterActive: boolean, title: string) => {
        rootStore.seasonsStore.setSeasonFilterActive(filterActive);
        rootStore.seasonsStore.setSelectedSeasonTitle(title);

        rootStore.episodesStore.fetchEpisodes({
          seasonIds,
          start: 0,
          limit: ALL_EPISODES_LIMIT,
          contentId: sheet.contentId ?? 0,
          contentType: rootStore.sheetStore.params.contentType,
          universeId: rootStore.sheetStore.params.universeId,
          providerCollectionId: rootStore.sheetStore.params.providerCollectionId,
          subProviderId: rootStore.sheetStore.params?.subProviderId ?? ''
        });
      },
      [sheet.contentId]
    );

    const handleSelectAll = React.useCallback(() => {
      rootStore.seasonsStore.setSelectedSeasonId(null);
      fetchForSeason(allSeasonIds, false, '');
    }, [allSeasonIds, fetchForSeason]);

    const handleSelectSeason = React.useCallback(
      (season: Seasons) => {
        rootStore.seasonsStore.setSelectedSeasonId(season.seasonIds);
        fetchForSeason([season.seasonIds], true, season.filterTitle);
      },
      [fetchForSeason]
    );

    const [prevArrowDisabled, setPrevArrowDisabled] = React.useState(true);
    const [nextArrowDisabled, setNextArrowDisabled] = React.useState(true);

    const updateArrowState = React.useCallback(() => {
      const element = ref.current;
      if (!element) return;

      const hasOverflow = element.scrollWidth > element.clientWidth + 1;
      if (!hasOverflow) {
        setPrevArrowDisabled(true);
        setNextArrowDisabled(true);
        return;
      }

      setPrevArrowDisabled(element.scrollLeft <= 0);
      setNextArrowDisabled(element.scrollLeft + element.clientWidth >= element.scrollWidth - 1);
    }, [ref]);

    React.useEffect(() => {
      const element = ref.current;
      if (!element) return;

      updateArrowState();
      const animationFrameId = window.requestAnimationFrame(updateArrowState);
      element.addEventListener('scroll', updateArrowState);
      window.addEventListener('resize', updateArrowState);

      return () => {
        window.cancelAnimationFrame(animationFrameId);
        element.removeEventListener('scroll', updateArrowState);
        window.removeEventListener('resize', updateArrowState);
      };
    }, [ref, hidden, seasons, updateArrowState]);

    const handleArrowScroll = React.useCallback(
      (forward: boolean) => {
        const el = ref.current;
        if (!el) return;
        const amount = (el.clientWidth * 4) / 5;
        el.scrollBy({ left: forward ? amount : -amount, behavior: 'smooth' });
      },
      [ref]
    );

    const handlePrevArrowClick = React.useCallback(() => {
      handleArrowScroll(false);
    }, [handleArrowScroll]);

    const handleNextArrowClick = React.useCallback(() => {
      handleArrowScroll(true);
    }, [handleArrowScroll]);

    if (hidden || !seasons || seasons.length <= 1) return null;

    return (
      <div className={styles['season-tabs-row']}>
        <FocusContext.Provider value={focusKey}>
          <DragScroll
            innerRef={ref as React.RefObject<HTMLDivElement>}
            direction="horizontal"
            disableDrag={!IS_BROWSER}
            hideScrollbar={true}
            className={styles['season-tabs']}
          >
            <div className={styles['season-tabs__content']}>
            <Button
              focusKey={SEASON_TAB_FOCUS_KEY_ALL}
              selected={selectedSeasonId === null}
              onClick={handleSelectAll}
              onArrowPress={handleFirstSeasonTabArrowPress}
              onNavFocus={handleSeasonTabFocus}
              className={styles['season-tab']}
              variant="tab"
            >
              Alle
            </Button>
            {seasons.map((season: Seasons) => (
              <Button
                key={season.seasonIds}
                focusKey={seasonTabFocusKey(season.seasonIds)}
                selected={selectedSeasonId === season.seasonIds}
                onClick={() => handleSelectSeason(season)}
                onArrowPress={handleSeasonTabArrowPress}
                onNavFocus={handleSeasonTabFocus}
                className={styles['season-tab']}
                variant="tab"
              >
                {season.filterTitle}
              </Button>
            ))}
            </div>
          </DragScroll>
        </FocusContext.Provider>
        {IS_BROWSER && (
          <div className={styles['season-tabs-row__actions']}>
            <IconButton
              className={clsx(
                styles['season-tabs-row__arrow'],
                styles['season-tabs-row__arrow--prev']
              )}
              size="sm"
              background={true}
              onClick={handlePrevArrowClick}
              disabled={prevArrowDisabled}
              aria-label="Forrige sæsoner"
              icon={<ArrowDownIcon />}
            />
            <IconButton
              className={clsx(
                styles['season-tabs-row__arrow'],
                styles['season-tabs-row__arrow--next']
              )}
              size="sm"
              background={true}
              onClick={handleNextArrowClick}
              disabled={nextArrowDisabled}
              aria-label="Næste sæsoner"
              icon={<ArrowDownIcon />}
            />
          </div>
        )}
      </div>
    );
  }
);

SheetEpisodeSelectorSeasonTabs.displayName = 'SheetEpisodeSelectorSeasonTabs';

export default SheetEpisodeSelectorSeasonTabs;
